import { useCallback, useState } from 'react';
import { logError } from 'utils/logger';
import { useNotifications } from 'modules/notification';
import { useMutation, useQuery } from '@apollo/client';
import { mapConditionsToCriteria } from 'modules/jsonRulesCriterias/businessCriteria';
import { mergeDefaultTierWithApiByIndex } from './lib';
import { TierCriteria, TierEntity, TierId } from './types';
import {
  CREATE_INTERNAL_TIERS,
  GET_INTERNAL_TIERS,
  TierCreateOrUpdateResponse,
  TierCreateVariables,
  TierResponse,
  TierUpdateVariables,
  TierVariables,
  UPDATE_CRITERIA_STATUS,
  UPDATE_INTERNAL_TIERS,
  UpdateCriteriaStatusResponse,
  UpdateCriteriaStatusVariables,
} from './api';

export function useTiers(accountId: string, onTiersReceived: (tiers: TierEntity[], tiersTypes: string[]) => void) {
  const { showNotification } = useNotifications();

  const [prescreenCriteria, setPrescreenCriteria] = useState<TierCriteria>({ id: null, isActive: false });

  const [tiers, setTiers] = useState<Record<TierId, TierEntity[]>>({});

  const { loading: isTiersLoading, refetch: refetchTiers } = useQuery<TierResponse, TierVariables>(GET_INTERNAL_TIERS, {
    variables: { accountId },
    onCompleted({ tiers }) {
      try {
        const normalizedTiersResponse = tiers.reduce<Record<TierId, TierEntity[]>>((acc, { id, prescreenCriteria }) => {
          if (Array.isArray(prescreenCriteria.jsonCriteria)) {
            setPrescreenCriteria({ id: prescreenCriteria.id, isActive: prescreenCriteria.active });

            acc[id] = prescreenCriteria.jsonCriteria.map((tier, index) => ({
              ...mergeDefaultTierWithApiByIndex(tier, index),
              criteria: mapConditionsToCriteria(tier?.formRules ?? []).reduce<TierEntity['criteria']>(
                (acc, { fact, boolean, string, number }) => {
                  acc![fact] = { boolean, string, number };
                  return acc;
                },
                {}
              ),
            }));

            return acc;
          }

          return acc;
        }, {});

        const tiersTypes = Object.values(tiers).reduce<Set<string>>((types, tier) => {
          if (Array.isArray(tier.prescreenCriteria.jsonCriteria)) {
            tier.prescreenCriteria.jsonCriteria.forEach(({ formRules }: any) => {
              formRules.forEach(({ fact }: any) => {
                types.add(fact);
              });
            });
          }
          return types;
        }, new Set<string>());

        setTiers(normalizedTiersResponse);
        onTiersReceived(Object.values(normalizedTiersResponse)?.[0] ?? [], Array.from(tiersTypes));
      } catch (error) {
        logError(error);
      }
    },
  });

  const [onCreateTiers, { loading: isCreating }] = useMutation<TierCreateOrUpdateResponse, TierCreateVariables>(
    CREATE_INTERNAL_TIERS,
    {
      onCompleted(data) {
        //refetchTiers(); // alternative approach would be to refetch tiers instead of set id manually
        if (data.createFinanceProgram?.prescreenCriteria) {
          setPrescreenCriteria({
            id: data.createFinanceProgram.prescreenCriteria.prescreenCriteriaId,
            isActive: true,
          });
        }
      },
    }
  );

  const [onUpdateTiers, { loading: isUpdating }] = useMutation<TierCreateOrUpdateResponse, TierUpdateVariables>(
    UPDATE_INTERNAL_TIERS
  );

  const [onUpdateCriteriaStatus, { loading: isCriteriaStatusUpdating }] = useMutation<
    UpdateCriteriaStatusResponse,
    UpdateCriteriaStatusVariables
  >(UPDATE_CRITERIA_STATUS);

  const handleCreateTiers = useCallback(
    async (createDTO: TierCreateVariables['input']) => {
      try {
        await onCreateTiers({ variables: { input: createDTO } });

        showNotification('Tiers Created');
      } catch (error) {
        showNotification('Error', { type: 'error' });
        logError(error);
      }
    },
    [onCreateTiers, showNotification]
  );

  const handleUpdateTiers = useCallback(
    async (updateDTO: TierUpdateVariables['input']) => {
      try {
        await onUpdateTiers({ variables: { input: updateDTO } });
        await refetchTiers();

        showNotification('Updated!');
      } catch (error) {
        showNotification('Error', { type: 'error' });
        logError(error);
      }
    },
    [onUpdateTiers, refetchTiers, showNotification]
  );

  const handleUpdateCriteriaStatus = useCallback(
    async (updateDTO: UpdateCriteriaStatusVariables['input']) => {
      try {
        await onUpdateCriteriaStatus({ variables: { input: updateDTO } });

        if (!updateDTO.active) {
          setPrescreenCriteria({ isActive: false, id: null });
        }

        await refetchTiers();

        showNotification('Status  updated!');
      } catch (error) {
        showNotification('Error', { type: 'error' });
        logError(error);
      }
    },
    [onUpdateCriteriaStatus, refetchTiers, showNotification]
  );

  return {
    isUpdating,
    isCreating,
    isTiersLoading,
    handleCreateTiers,
    handleUpdateTiers,
    isCriteriaStatusUpdating,
    handleUpdateCriteriaStatus,
    tierId: prescreenCriteria.id,
    tiers: Object.values(tiers)?.[0],
    isLoading: isCreating || isUpdating || isTiersLoading,
    prescreenCriteria: { ...prescreenCriteria, id: Object.keys(tiers)?.[0] ?? null },
  };
}
